import { getAnalysisSubtitle } from '@modules/analysis/helpers';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import {
  useGetDefaultTenantTimeRange,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { replaceDateObjectWithTimeStamp } from '@modules/common/utils';
import { getCombinedWhereClause } from '@modules/dashboard/helpers';
import { AnalysisViewFragment } from 'generated/graphql';
import { useDeepCompareEffect } from 'react-use';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import {
  getAnalysisLink,
  getAnalysisRoute,
} from 'v2/core/services/analysis/link';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { getSearchMetadataUrl, getSearchQueryFromMetadata } from '../utils';

interface useSetAnalysisStoreFromFeedProps {
  analysis?: AnalysisViewFragment;
  overrideAnalysisMetadata?: {
    duration?: DateTimeRange;
    whereClause?: WhereClause;
  };
}

export const useSetAnalysisStoreFromFeed = ({
  analysis,
  overrideAnalysisMetadata,
}: useSetAnalysisStoreFromFeedProps) => {
  const user = useGetUser();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const { setAnalysis, ...storedAnalysis } = useAnalysisStore();
  const defaultTimeRange = useGetDefaultTenantTimeRange();
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  useDeepCompareEffect(() => {
    if (!analysis) {
      return;
    }

    const {
      title,
      viewToken,
      editToken,
      createdBy,
      updatedAt,
      metadata,
      context,
    } = analysis;

    let updatedMetadata = metadata;

    const searchQuery = getSearchQueryFromMetadata({
      metadata,
      getDurationField: true,
      defaultTimeRange,
      eqlProperties: context?.eql?.properties,
      sourceDetailsMap,
    });

    if (overrideAnalysisMetadata) {
      updatedMetadata = getSearchMetadataUrl({
        isSearchToFeedEnabled,
        ...searchQuery,
        whereClause:
          overrideAnalysisMetadata.whereClause && searchQuery.whereClause
            ? getCombinedWhereClause(
                searchQuery.whereClause,
                overrideAnalysisMetadata.whereClause,
              )
            : searchQuery.whereClause ?? null,
        duration: overrideAnalysisMetadata.duration
          ? (replaceDateObjectWithTimeStamp(
              overrideAnalysisMetadata.duration,
            ) as DateTimeRange)
          : searchQuery.duration ?? defaultTimeRange,
      });
    }

    const analysisValues = {
      ...storedAnalysis,
      ...analysis,
      metadata: updatedMetadata,
      isEditable: Boolean(editToken),
      subtitle: getAnalysisSubtitle({
        userName: `${createdBy.firstName} ${createdBy.lastName}`,
        updatedAt: updatedAt,
      }),
      href: getAnalysisLink({
        name: title,
        token: viewToken,
        tenantSlug: user.tenant.slug,
        useCase: analysis.useCase,
      }),
      path: getAnalysisRoute({
        name: title,
        token: viewToken,
        useCase: analysis.useCase,
      }),
      tenantID: user.tenant.id,
      title: title ?? `Untitled Analysis`,
      isLoading: false,
    };
    setAnalysis(analysisValues);
  }, [
    analysis,
    user.firstName,
    user.id,
    user.lastName,
    user.tenant.id,
    user.tenant.slug,
    overrideAnalysisMetadata,
  ]);
};
